import React, { useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { useDispatch, useSelector } from 'react-redux';
import { useNonInitialEffect } from '@versiondos/hooks';
import Marketplace, { SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';
import {
    MarketplaceStatus,
    PPP_ADD_ADDONS_ENABLED_KEY,
    PPP_CANCELLATION_ENABLED_KEY,
    savePppAddAddonsThunk,
    savePppCancellationThunk
} from '@spike/marketplace-action';
import { RootState } from 'store';
import { useSettingsApiClientWrapper } from 'hooks';
import { TextFieldWithButton } from 'components/UI';
import Switch from 'components/UI/V2/Switch';
import BusinessSettingsTitle from '../UI/BusinessSettingsTitle';
import styles from './PetParentPortal.module.scss';

export interface PetParentPortalProps {
    className?: string;
    openSidebar?: () => void;
    isMobile?: boolean;
}

export const PetParentPortal: React.FC<PetParentPortalProps> = ({ className, openSidebar, isMobile }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useSettingsApiClientWrapper();

    const marketplace = useSelector<RootState, Marketplace>(state => state.marketplace.settingsMarketplace);
    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const savedCancellationEnabled =
        marketplace.settings.find(setting => setting.key === PPP_CANCELLATION_ENABLED_KEY)?.value ===
        SETTINGS_TRUE_VALUE;

    const savedAddAddonsEnabled =
        marketplace.settings.find(setting => setting.key === PPP_ADD_ADDONS_ENABLED_KEY)?.value === SETTINGS_TRUE_VALUE;

    const petParentPortalUrl = `${process.env.REACT_APP_PET_PARENT_PORTAL_HOST_URL}${marketplace.onlineBooking.slug}`;

    const [cancellationEnabled, setCancellationEnabled] = useState(savedCancellationEnabled);
    const [addAddonsEnabled, setAddAddonsEnabled] = useState(savedAddAddonsEnabled);
    const [copied, setCopied] = useState(false);

    useNonInitialEffect(() => {
        if (status === MarketplaceStatus.Error) {
            setCancellationEnabled(savedCancellationEnabled);
            setAddAddonsEnabled(savedAddAddonsEnabled);
        }
    }, [status]);

    const copyToClipboard = () => {
        setCopied(true);
        if ('clipboard' in navigator) {
            navigator.clipboard.writeText(petParentPortalUrl);
        } else {
            document.execCommand('copy', true, petParentPortalUrl);
        }
    };

    const changeCancellationHandler = (_name: string, checked: boolean) => {
        setCancellationEnabled(checked);
        dispatch(savePppCancellationThunk(apiClientWrapper, checked));
    };

    const changeAddAddonsHandler = (_name: string, checked: boolean) => {
        setAddAddonsEnabled(checked);
        dispatch(savePppAddAddonsThunk(apiClientWrapper, checked));
    };

    return (
        <div className={clsx(styles.container, className)}>
            <div className={styles.titleContainer}>
                <BusinessSettingsTitle className={styles.businessSettingsTitle}>
                    {isMobile && (
                        <FontAwesomeIcon
                            className={styles.iconBusinessTitle}
                            onClick={openSidebar}
                            icon={faArrowLeft}
                            size="2x"
                        />
                    )}
                    Pet Parent Portal
                </BusinessSettingsTitle>
                <p className={styles.description}>
                    Share your Pet Parent Link and let clients track appointments, access care history, and stay
                    connected to their pets.
                </p>
            </div>

            <div className={styles.linkContainer}>
                <TextFieldWithButton
                    value={petParentPortalUrl}
                    label={copied ? 'Copied' : 'Copy'}
                    startIcon={copied ? faCheck : faCopy}
                    blackButton={true}
                    onClick={copyToClipboard}
                />
            </div>

            <p className={styles.sectionTitle}>Booking Permissions</p>
            <p className={styles.sectionDescription}>Control what actions pet parents can manage.</p>

            <div className={styles.permissionRow}>
                <p className={styles.permissionLabel}>Allow pet parents to cancel appointments</p>
                <Switch
                    name="ppp_cancellation_enabled"
                    checked={cancellationEnabled}
                    onCheck={changeCancellationHandler}
                />
            </div>

            <div className={styles.permissionRow}>
                <p className={styles.permissionLabel}>Allow clients to add Add-Ons to existing bookings</p>
                <Switch
                    name="ppp_add_addons_enabled"
                    checked={addAddonsEnabled}
                    onCheck={changeAddAddonsHandler}
                />
            </div>
        </div>
    );
};

export default PetParentPortal;
